import { useState, type FormEvent, type ReactNode } from "react";
import type { SecondStepChallenge, Session } from "./auth.js";
import { describeAuthError, normaliseCode } from "./password.js";

/**
 * The code after the password (#92), from the authenticator app. Used by the
 * sign-in screen and by the tablet's
 * password prompt (StepUp.tsx), which is why it is only the form.
 */
export function SecondStepForm(props: {
  readonly challenge: SecondStepChallenge;
  readonly onSignedIn: (s: Session) => void;
  readonly onCancel: () => void;
  /** Drawn at the top of the form, which is the card on the sign-in screen. */
  readonly heading?: ReactNode;
}) {
  const { challenge } = props;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = normaliseCode(code);
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      props.onSignedIn(await challenge.complete(value));
    } catch (err) {
      setError(describeAuthError(err, "second-step"));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="second-step" onSubmit={submit}>
      {props.heading}
      <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
        Enter the code your authenticator app shows for Bar OS.
      </div>
      <label className="field">
        <span>Code</span>
        <input
          autoComplete="one-time-code"
          inputMode="numeric"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
        />
      </label>
      {error && <div className="msg err">{error}</div>}
      <button className="btn primary" type="submit" disabled={busy || !normaliseCode(code)}>
        {busy ? "Checking…" : "Sign in"}
      </button>
      <button className="btn" type="button" onClick={props.onCancel} disabled={busy}>
        Start again
      </button>
    </form>
  );
}
